import React, { useState, useEffect } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import AOS from "aos";
import "aos/dist/aos.css";
import { useTranslation } from "react-i18next";
import Header from "./../../components/UserSideComponents/Category/Header";
import SubCategory from "./../../components/UserSideComponents/Category/SubCategory";
import SpecialProducts from "./../../components/UserSideComponents/Category/SpecialProducts";
import Trending from "./../../components/UserSideComponents/Category/Trending";
import WoodenBuyingGuides from "./../../components/UserSideComponents/Category/WoodenBuyingGuides";
import FAQSection from "./../../components/UserSideComponents/Category/FAQSection";
import HeaderSkeleton from "../../components/UserSideComponents/Category/HeaderSkeleton";
import SubCategorySkeleton from "../../components/UserSideComponents/Category/SubCategorySkeleton";
import ProductSkeleton from "../../components/UserSideComponents/Category/ProductSkeleton";

const Category = () => {
  const { id } = useParams();
  const { t, i18n } = useTranslation();
  const currentLanguage = i18n.language || "en";
  const [selectedSubCategory, setSelectedSubCategory] = useState(null);
  const [category, setCategory] = useState(null);
  const [subCategories, setSubCategories] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    AOS.init({ duration: 800, easing: "ease-out", once: true });
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  // Helper function to get localized text
  const getLocalizedText = (item, field) => {
    if (!item || !item[field]) return "";
    
    if (typeof item[field] === 'string') {
      return item[field];
    }
    
    if (typeof item[field] === 'object') {
      return item[field][currentLanguage] || 
             item[field].en || 
             item[field].hi || 
             item[field].es || 
             item[field].zh || 
             "";
    }
    
    return "";
  };

  useEffect(() => {
    const loadData = async () => {
      try {
        setLoading(true);

        // 1. GET CATEGORY
        const catRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/categories/get-category-by-id/${id}`
        );

        const categoryData = catRes?.data;
        setCategory(categoryData);

        // 2. GET SUBCATEGORIES
        const subRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/sub-categories/get-sub-categories-by-category/${id}`
        );
        setSubCategories(subRes.data.data || subRes.data);

        // 3. GET PRODUCTS
        const prodRes = await axios.get(
          `${process.env.REACT_APP_API_URL}/products/sub-categories-products-by-category/${id}`
        );

        // API returns → { data: [ {subCategory, products} ] }
        const formattedProducts = [];

        (prodRes.data.data || []).forEach((subcat) => {
          if (Array.isArray(subcat.products)) {
            subcat.products.forEach((p) => {
              const productName = getLocalizedText(p, "name");
              const subCategoryName = getLocalizedText(subcat.subCategory, "name");
              
             // Inside the forEach in Category.jsx — add subCategory to each product:
formattedProducts.push({
  id: p._id,
  _id: p._id,
  name: productName,
  nameObj: p.name,
  ratings: p.ratings,
  price: p.price,
  previous_price: p.previous_price,
  image: p.images?.[0],
  images: p.images,
  isBestProduct: p.isBestProduct,
  category: subCategoryName,
  categoryObj: subcat.subCategory,
  description: getLocalizedText(p, "description"),
  descriptionObj: p.description,
  material: p.material,        // ← pass the raw object, not getLocalizedText
  measurement: p.measurement,  // ← raw object
  height: p.height,            // ← plain string
  weight: p.weight,            // ← plain string
  brand: p.brand,              // ← plain string
  subCategory: p.subCategory,  // ← THIS is the key fix for subcategory filter
  stock_status: p.stock_status,
  createdAt: p.createdAt,
});
            });
          }
        });

        setProducts(formattedProducts);
      } catch (err) {
        console.error("Error loading category:", err);
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [id]);

  // Re-fetch localized data when language changes
  useEffect(() => {
    if (category || products.length > 0) {
      // Update category with current language
      if (category) {
        const updatedCategory = {
          ...category,
          localizedName: getLocalizedText(category, "name"),
          localizedDescription: getLocalizedText(category, "description")
        };
        setCategory(updatedCategory);
      }

      // Update products with current language
      const updatedProducts = products.map(product => ({
        ...product,
        name: getLocalizedText(product?.nameObj || product?.name, "name"),
        category: getLocalizedText(product.categoryObj || product.category, "name"),
        description: getLocalizedText(product.descriptionObj || product.description, "description"),
        material: getLocalizedText(product.materialObj || product.material, "material"),
      }));
      setProducts(updatedProducts);
    }
  }, [currentLanguage]);

  if (loading) {
    return (
      <div className="overflow-hidden">
        <HeaderSkeleton />
        <SubCategorySkeleton />
        <div className="bg-gradient-to-b from-orange-50 to-white px-4 md:px-10 pb-10">
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6 mt-6">
            {[...Array(10)].map((_, i) => (
              <ProductSkeleton key={i} />
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (!category) return <div className="p-10 text-center">{t("category_not_found") || "Category not found"}</div>;

  return (
    <div className="overflow-hidden">
      <Header category={category} />
   <SubCategory
  subcategories={subCategories}
  selectedSubCategory={selectedSubCategory}
  onSelect={setSelectedSubCategory}
/>
<SpecialProducts
  products={products}
  subcategories={subCategories}
  selectedSubCategoryFromParent={selectedSubCategory}
  onSubCategoryChange={setSelectedSubCategory}
/>
      {/* <Trending /> */}
      {/* <WoodenBuyingGuides /> */}
      {/* <FAQSection /> */}
    </div>
  );
};

export default Category;




// import React, { useState, useEffect } from "react";
// import { useParams } from "react-router-dom";
// import axios from "axios";
// import AOS from "aos";
// import "aos/dist/aos.css";
// import Header from "./../../components/UserSideComponents/Category/Header";
// import SubCategory from "./../../components/UserSideComponents/Category/SubCategory";
// import SpecialProducts from "./../../components/UserSideComponents/Category/SpecialProducts";
// import Trending from "./../../components/UserSideComponents/Category/Trending";
// import WoodenBuyingGuides from "./../../components/UserSideComponents/Category/WoodenBuyingGuides";
// import FAQSection from "./../../components/UserSideComponents/Category/FAQSection";
// import HeaderSkeleton from "../../components/UserSideComponents/Category/HeaderSkeleton";
// import SubCategorySkeleton from "../../components/UserSideComponents/Category/SubCategorySkeleton";
// import ProductSkeleton from "../../components/UserSideComponents/Category/ProductSkeleton";

// const Category = () => {
//   const { id } = useParams();
//   const [category, setCategory] = useState(null);
//   const [subCategories, setSubCategories] = useState([]);
//   const [products, setProducts] = useState([]);
//   const [loading, setLoading] = useState(true);
//   useEffect(() => {
//     AOS.init({ duration: 800, easing: "ease-out", once: true });
//     window.scrollTo({ top: 0, behavior: "smooth" });
//   }, []);

//   useEffect(() => {
//     const loadData = async () => {
//       try {
//         setLoading(true);

//         // 1. GET CATEGORY (your API returns array → we take index 0)
//         const catRes = await axios.get(
//           `${process.env.REACT_APP_API_URL}/categories/get-category-by-id/${id}`
//         );

//         const categoryData = catRes?.data;

//         setCategory(categoryData);

//         //  2. GET SUBCATEGORIES
//         const subRes = await axios.get(
//           `${process.env.REACT_APP_API_URL}/sub-categories/get-sub-categories-by-category/${id}`
//         );
//         setSubCategories(subRes.data.data || subRes.data);

//         // 3. GET PRODUCTS
//         const prodRes = await axios.get(
//           `${process.env.REACT_APP_API_URL}/products/sub-categories-products-by-category/${id}`
//         );

//         // API returns → { data: [ {subCategory, products} ] }
//         const formattedProducts = [];

//         (prodRes.data.data || []).forEach((subcat) => {
//           if (Array.isArray(subcat.products)) {
//             subcat.products.forEach((p) =>
//               formattedProducts.push({
//                 id: p._id,
//                 name: p.name,
//                 ratings: p.ratings,
//                 price: p.price,
//                 previous_price: p.previous_price,
//                 image: p.images?.[0],
//                 isBestProduct: p.isBestProduct,
//                 category: subcat.subCategory?.name,
//               })
//             );
//           }
//         });

//         setProducts(formattedProducts);
//       } catch (err) {
//         console.error("Error loading category:", err);
//       } finally {
//         setLoading(false);
//       }
//     };

//     loadData();
//   }, [id]);

//   if (loading) {
//     return (
//       <div className="overflow-hidden">
//         <HeaderSkeleton />
//         <SubCategorySkeleton />

//         <div className="bg-gradient-to-b from-orange-50 to-white px-4 md:px-10 pb-10">
//           <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6 mt-6">
//             {[...Array(10)].map((_, i) => (
//               <ProductSkeleton key={i} />
//             ))}
//           </div>
//         </div>
//       </div>
//     );
//   }

//   if (!category) return <div className="p-10 text-center">Category not found</div>;

//   return (
//     <div className="overflow-hidden">

//       <Header category={category} />

//       <SubCategory subcategories={subCategories} />

//       <SpecialProducts products={products} />

//       <Trending products={products} />

//       <WoodenBuyingGuides />
//       <FAQSection />
//     </div>
//   );
// };

// export default Category;
